// src/components/AnchoredMenu.tsx
// The app's popover menu: rendered in a body portal, positioned against a
// trigger element or a bare cursor point by useAnchoredPosition.
//
// Why a portal: a popover positioned inside a scrolling list is clipped by that
// list and inflates its scrollHeight, so a tall menu ends up with a scrollbar
// of its own and scrolls along with the content behind it. Fixed positioning in
// a body portal keeps it out of every ancestor's overflow.
//
// It carries menu semantics by default but can render as a listbox or a plain
// panel (`role`/`as`), because selects and pickers need those — the alternative
// was each of them keeping its own portal and positioning code, which is how
// they drifted apart in the first place.

import { forwardRef, useCallback, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';
import { useDismissOnOutside } from '@/hooks/useDismissOnOutside';
import {
  hiddenUntilPositioned,
  isPointAnchor,
  useAnchoredPosition,
  type Anchor,
  type AnchoredPositionOptions,
} from './useAnchoredPosition';

export type { Anchor } from './useAnchoredPosition';

export interface AnchoredMenuProps extends AnchoredPositionOptions {
  anchor: Anchor;
  open: boolean;
  onClose: () => void;
  /** ARIA role of the container. `listbox` expects `option` children. */
  role?: 'menu' | 'listbox' | 'dialog' | 'group' | 'none';
  /** Element to render. `ul` for option lists, so `li` children stay valid. */
  as?: 'div' | 'ul';
  id?: string;
  ariaLabel?: string;
  /** Id of the active descendant, for keyboard-navigated option lists. */
  ariaActivedescendant?: string;
  /**
   * Extra element whose clicks must not dismiss — a trigger's wrapper, say,
   * when the label next to the button counts as part of the control.
   */
  ignoreRef?: RefObject<HTMLElement | null>;
  /** Opt out when the caller already handles Escape (e.g. on its trigger). */
  closeOnEscape?: boolean;
  /**
   * Opt out when the caller's dismissal is more subtle than "click landed
   * elsewhere" — e.g. a panel hosting other pickers, whose own portals must not
   * count as outside.
   */
  closeOnOutsideClick?: boolean;
  /** Focus-out dismissal, for popovers that close when focus leaves them. */
  onBlur?: React.FocusEventHandler<HTMLElement>;
  className?: string;
  children: ReactNode;
}

export const AnchoredMenu = forwardRef<HTMLElement, AnchoredMenuProps>(function AnchoredMenu(
  {
    anchor,
    open,
    onClose,
    role = 'menu',
    as = 'div',
    id,
    ariaLabel,
    ariaActivedescendant,
    ignoreRef,
    closeOnEscape = true,
    closeOnOutsideClick = true,
    onBlur,
    className,
    children,
    ...position
  },
  forwardedRef,
) {
  const menuRef = useRef<HTMLElement | null>(null);
  const style = useAnchoredPosition(anchor, open, menuRef, position);

  // A press on the trigger must not close-then-reopen: the trigger's own
  // handler does the toggling, so it counts as inside. An element anchor is
  // covered by its ref; an anchor given as a rect — a trigger captured when the
  // popover opened, as the tab strip does — is covered by hit-testing that rect,
  // which is the only handle on the trigger there is. Dismissal itself is the
  // app-wide one.
  const anchorRef = isPointAnchor(anchor) || anchor instanceof DOMRect ? null : anchor;
  const inAnchorRect = useCallback(
    (event: PointerEvent) =>
      anchor instanceof DOMRect &&
      event.clientX >= anchor.left &&
      event.clientX <= anchor.right &&
      event.clientY >= anchor.top &&
      event.clientY <= anchor.bottom,
    [anchor],
  );
  useDismissOnOutside([menuRef, anchorRef, ignoreRef], open, onClose, {
    onOutsideClick: closeOnOutsideClick,
    onEscape: closeOnEscape,
    isInside: inAnchorRect,
  });

  if (!open) return null;

  const setRef = (el: HTMLElement | null) => {
    menuRef.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  const Tag = as as 'div';

  return createPortal(
    <Tag
      ref={setRef as React.Ref<HTMLDivElement>}
      id={id}
      role={role === 'none' ? undefined : role}
      aria-label={ariaLabel}
      aria-activedescendant={ariaActivedescendant}
      onBlur={onBlur}
      // An element that drives focus through aria-activedescendant has to be
      // focusable itself, even if only programmatically.
      tabIndex={ariaActivedescendant === undefined && role !== 'listbox' ? undefined : -1}
      style={style ?? hiddenUntilPositioned}
      // `cn` is a plain join, not tailwind-merge, so a base class a caller wants
      // to drop can't be overridden — padding is the caller's to set.
      className={cn(
        'z-50 overflow-y-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-lg',
        className,
      )}
    >
      {children}
    </Tag>,
    document.body,
  );
});

export interface MenuItemProps {
  icon?: ReactNode;
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export function MenuItem({ icon, label, onSelect, destructive, disabled }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50',
        destructive ? 'hover:text-destructive' : 'hover:text-foreground',
      )}
    >
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

export function MenuSeparator() {
  return <div className="my-1 border-t border-border" role="separator" />;
}
